/**
 * Dashboard "Recent activity" = short preview of that role's real activity surface.
 *
 * Admin/billing widgets read curated `audit_events` (same source as Historial).
 * Field roles keep job/no-service snapshots elsewhere — do not route them here.
 *
 * Event-type strings collide across entities (`sent`, `created`, …), so the
 * allowlist is always `${entity_type}:${event_type}` pairs. Fetch by entity
 * types, then filter client-side with these keys.
 */

/** High-signal org timeline for admin Panel → Historial continuity. */
export const ADMIN_DASHBOARD_ACTIVITY_KEYS = Object.freeze([
  'invoice:paid',
  'invoice:sent',
  'invoice:voided',
  'job:logged',
  'job:job_logged_on_behalf_of',
  'no_service:created',
  'estimate:accepted',
  'estimate:converted_to_invoice',
  'estimate:declined',
]);

/** Billing RLS already scopes audit reads; keep invoice/estimate signal only. */
export const BILLING_DASHBOARD_ACTIVITY_KEYS = Object.freeze([
  'invoice:paid',
  'invoice:sent',
  'invoice:voided',
  'invoice:created',
  'estimate:sent',
  'estimate:accepted',
  'estimate:declined',
  'estimate:converted_to_invoice',
]);

const PROFILE_KEYS = Object.freeze({
  admin: ADMIN_DASHBOARD_ACTIVITY_KEYS,
  billing: BILLING_DASHBOARD_ACTIVITY_KEYS,
});

/** Entity types to request from PostgREST for a dashboard profile. */
export function dashboardActivityEntityTypes(profile) {
  if (profile === 'billing') return ['invoice', 'estimate'];
  // admin (default)
  return ['invoice', 'job', 'no_service', 'estimate'];
}

export function dashboardActivityAllowlist(profile) {
  return PROFILE_KEYS[profile] || ADMIN_DASHBOARD_ACTIVITY_KEYS;
}

export function auditEventKey(event) {
  if (!event?.entity_type || !event?.event_type) return '';
  return `${event.entity_type}:${event.event_type}`;
}

/** Rows the dashboard Recent-activity widget shows; also part of the store key. */
export const DASHBOARD_ACTIVITY_WIDGET_LIMIT = 8;

/**
 * Keep allowlisted events, newest-first (input assumed already newest-first),
 * capped for the widget.
 */
export function filterDashboardAuditEvents(events, profile, limit = DASHBOARD_ACTIVITY_WIDGET_LIMIT) {
  const allow = new Set(dashboardActivityAllowlist(profile));
  const out = [];
  for (const event of events || []) {
    if (!allow.has(auditEventKey(event))) continue;
    out.push(event);
    if (out.length >= limit) break;
  }
  return out;
}

/** Amount shown on the right of a widget row, when payload carries one. */
export function auditEventAmount(event) {
  const p = event?.payload || {};
  if (event?.entity_type === 'invoice' || event?.entity_type === 'estimate') {
    const n = Number(p.total);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  if (event?.entity_type === 'job') {
    const n = Number(p.job_total != null ? p.job_total : p.total);
    return Number.isFinite(n) && n > 0 ? n : null;
  }
  return null;
}

/** How many raw rows to fetch before client allowlist filtering. */
export const DASHBOARD_ACTIVITY_FETCH_LIMIT = 40;
